import axios from "axios";
import { apiUrl } from "../config/environment";
import { getAuthHeaders } from "../utils/authUtils";

const apiBase = `${apiUrl}/api/inventory-uploads`;

export async function createUploadJob(totalRows?: number, originalFilename?: string) {
  // [2025-12-20] Tracing: Capture client device info
  const { captureClientDeviceInfo } = require('../utils/captureClientDeviceInfo');
  const clientDeviceInfo = captureClientDeviceInfo();
  
  const res = await axios.post(
    `${apiBase}/jobs`,
    { totalRows: totalRows ?? 0, originalFilename: originalFilename || null, clientDeviceInfo },
    getAuthHeaders(),
  );
  return res.data as { jobId: string; status: string; traceId?: string };
}

export async function pushChunk(jobId: string, seq: number, rows: any[]) {
  const res = await axios.post(
    `${apiBase}/jobs/${jobId}/chunks`,
    { seq, rows },
    getAuthHeaders(),
  );
  return res.data as { accepted: number; seq: number };
}

// [2026-09-17] UPLOAD_ABORT: tell the server the browser has given up, so the job ends as
// FAILED instead of sitting at PENDING while the screen shows "Queued - waiting to start".
export async function abortJob(jobId: string, reason: string) {
  const res = await axios.post(`${apiBase}/jobs/${jobId}/abort`, { reason }, getAuthHeaders());
  return res.data as { status: string; chunksRemoved: number };
}

export async function completeJob(jobId: string) {
  const res = await axios.post(`${apiBase}/jobs/${jobId}/complete`, {}, getAuthHeaders());
  return res.data as { jobId: string; status: string };
}

export async function getJobStats(jobId: string) {
  const res = await axios.get(`${apiBase}/jobs/${jobId}/stats`, getAuthHeaders());
  return res.data;
}

/**
 * [2026-09-16] Find an upload of mine that has not finished yet.
 * The job id used to live only in component state, so navigating away or reloading lost the
 * progress panel while the upload carried on server-side - the page then showed a partially
 * loaded catalog that looked complete. The database is the source of truth, so ask it.
 */
export async function getActiveJob(): Promise<{ jobId: string } | null> {
  const res = await axios.get(`${apiBase}/jobs`, getAuthHeaders());
  const rows = (res.data as any[]) || [];
  const running = rows.find(
    (j) => !j.finishedAt && !["DONE", "PARTIAL", "FAILED", "CANCELLED"].includes(j.status),
  );
  if (running) return { jobId: String(running.jobId) };
  // [2026-09-17] A job that failed used to disappear on reload, so a customer who refreshed saw
  // an unchanged catalogue and no explanation at all. Keep a recent failure on screen long
  // enough to be read.
  const recentFailure = rows.find(
    (j) =>
      j.status === "FAILED" &&
      j.finishedAt &&
      Date.now() - new Date(j.finishedAt).getTime() < 60 * 60 * 1000,
  );
  return recentFailure ? { jobId: String(recentFailure.jobId) } : null;
}
